import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Card,
  CardContent,
  Stack,
  Collapse,
  TableSortLabel,
  Tooltip,
  Avatar,
  SelectChangeEvent,
  ListItemText,
  List,
  ListItem,
  ListItemSecondaryAction,
  Checkbox,
  RadioGroup,
  Radio,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Store as StoreIcon,
  Clear as ClearIcon,
  FileDownload as FileDownloadIcon,
  Payments as PaymentsIcon,
  Payment as PaymentIcon,
  History as HistoryIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
// import { collection, addDoc, getDocs, doc, updateDoc, deleteDoc, query, where, orderBy, increment } from 'firebase-shim';
// import { db } from '../firebase/config';
import * as indexedDBService from '../utils/indexedDBService';
import { Vendor, PAYMENT_TERMS, VENDOR_TYPES, PAYMENT_METHODS, VendorPayment } from '../types/vendor';
import { Purchase } from '../types/purchase';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { formatDate, formatCurrency } from '../utils/formatters';
import { formatGSTN, isValidGSTN } from '../utils/gstUtils';
import AnimatedPage from '../components/AnimatedPage';

// Define transaction interface
interface Transaction {
  id: string;
  date: string;
  type: 'purchase' | 'payment';
  amount: number;
  reference: string;
  status: string;
  balance: number;
}

const Vendors: React.FC = () => {
  const { currentUser } = useAuth();
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [searchQuery, setSearchQuery] = useState('');
  const [newVendor, setNewVendor] = useState<Partial<Vendor>>({
    name: '',
    contactPerson: '',
    email: '',
    phone: '',
    gstin: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    paymentTerms: '30 Days',
    accountNumber: '',
    bankName: '',
    ifscCode: '',
    vendorType: 'Product Supplier',
  });
  const [vendorPayables, setVendorPayables] = useState<{ [vendorId: string]: number }>({});
  const [totalPayables, setTotalPayables] = useState(0);
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  }>({ key: 'name', direction: 'asc' });
  const [expandedVendor, setExpandedVendor] = useState<string | null>(null);
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [selectedVendorForPayment, setSelectedVendorForPayment] = useState<Vendor | null>(null);
  const [pendingPurchases, setPendingPurchases] = useState<Purchase[]>([]);
  const [selectedPurchases, setSelectedPurchases] = useState<string[]>([]);
  const [paymentAmount, setPaymentAmount] = useState<number>(0);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentDate, setPaymentDate] = useState<Date | null>(new Date());
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [paymentAllocation, setPaymentAllocation] = useState<'auto' | 'manual'>('auto');

  // Ledger related states
  const [openLedgerDialog, setOpenLedgerDialog] = useState(false);
  const [ledgerVendor, setLedgerVendor] = useState<Vendor | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [startDate, setStartDate] = useState<Date | null>(new Date(new Date().getFullYear(), new Date().getMonth() - 3, 1));
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [transactionTypeFilter, setTransactionTypeFilter] = useState<string[]>([]);
  const [transactionStatusFilter, setTransactionStatusFilter] = useState<string[]>([]);

  const fetchVendors = useCallback(async () => {
    setLoading(true);
    try {
      const fetchedVendors = await indexedDBService.getAllItems<Vendor>('vendors');
      setVendors(fetchedVendors);
    } catch (error) {
      console.error('Error fetching vendors:', error);
      setSnackbar({
        open: true,
        message: 'Error loading vendors',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // Fetch vendor payables
  const fetchVendorPayables = useCallback(async () => {
    if (!currentUser) return;

    try {
      const purchases = await indexedDBService.getAllItems<Purchase>('purchases');
      const pendingPurchases = purchases.filter(p => p.status === 'pending');

      const payables: { [vendorId: string]: number } = {};
      let total = 0;

      pendingPurchases.forEach(purchase => {
        const vendorId = purchase.vendorId;
        const amount = purchase.totalAmount || 0;

        if (vendorId) {
          payables[vendorId] = (payables[vendorId] || 0) + amount;
          total += amount;
        }
      });

      setVendorPayables(payables);
      setTotalPayables(total);
    } catch (error) {
      console.error('Error fetching vendor payables:', error);
    }
  }, [currentUser]);

  // Fetch pending purchases for a specific vendor
  const fetchPendingPurchases = async (vendorId: string) => {
    if (!currentUser) return;

    try {
      const allPurchases = await indexedDBService.getAllItems<Purchase>('purchases');
      const purchases = allPurchases.filter(p =>
        p.vendorId === vendorId &&
        p.status === 'pending'
      );

      // Sort by due date
      purchases.sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime());

      setPendingPurchases(purchases);

      // By default, select all purchases
      setSelectedPurchases(purchases.map(p => p.id));

      // Set default payment amount to total of all pending purchases
      const totalPending = purchases.reduce((sum, p) => sum + (p.totalAmount - (p.paidAmount || 0)), 0);
      setPaymentAmount(totalPending);
    } catch (error) {
      console.error('Error fetching pending purchases:', error);
    }
  };

  useEffect(() => {
    if (currentUser) {
      fetchVendors();
      fetchVendorPayables();
    }
  }, [currentUser, fetchVendors, fetchVendorPayables]);

  const handleOpenDialog = (vendor?: Vendor) => {
    if (vendor) {
      setSelectedVendor(vendor);
      setNewVendor(vendor);
    } else {
      setSelectedVendor(null);
      setNewVendor({
        name: '',
        contactPerson: '',
        email: '',
        phone: '',
        gstin: '',
        address: '',
        city: '',
        state: '',
        pincode: '',
        paymentTerms: '30 Days',
        accountNumber: '',
        bankName: '',
        ifscCode: '',
        vendorType: 'Product Supplier',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedVendor(null);
    setNewVendor({
      name: '',
      contactPerson: '',
      email: '',
      phone: '',
      gstin: '',
      address: '',
      city: '',
      state: '',
      pincode: '',
      paymentTerms: '30 Days',
      accountNumber: '',
      bankName: '',
      ifscCode: '',
      vendorType: 'Product Supplier',
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNewVendor({
      ...newVendor,
      [name]: value
    });
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setNewVendor({
      ...newVendor,
      [name as string]: value
    });
  };

  const handleSaveVendor = async () => {
    if (!currentUser) return;

    // Validate required fields
    if (!newVendor.name) {
      setSnackbar({
        open: true,
        message: 'Vendor name is required',
        severity: 'error'
      });
      return;
    }

    try {
      const vendorData = {
        ...newVendor,
        userId: currentUser.uid,
        updatedAt: new Date().toISOString(),
        createdAt: selectedVendor ? selectedVendor.createdAt : new Date().toISOString(),
      };

      let vendorId = selectedVendor?.id;
      if (!vendorId) {
        vendorId = 'vendor_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      }

      await indexedDBService.putItem('vendors', {
        ...vendorData,
        id: vendorId
      });

      setSnackbar({
        open: true,
        message: `Vendor ${selectedVendor ? 'updated' : 'added'} successfully`,
        severity: 'success',
      });
      handleCloseDialog();
      fetchVendors();
    } catch (error) {
      console.error('Error saving vendor:', error);
      setSnackbar({
        open: true,
        message: 'Error saving vendor',
        severity: 'error'
      });
    }
  };

  const handleDeleteVendor = async (vendorId: string) => {
    if (window.confirm('Are you sure you want to delete this vendor?')) {
      try {
        await indexedDBService.deleteItem('vendors', vendorId);
        await fetchVendors();
        setSnackbar({
          open: true,
          message: 'Vendor deleted successfully',
          severity: 'success'
        });
      } catch (error) {
        console.error('Error deleting vendor:', error);
        setSnackbar({
          open: true,
          message: 'Error deleting vendor',
          severity: 'error'
        });
      }
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Ledger functionality
  const handleOpenLedgerDialog = async (vendor: Vendor) => {
    setLedgerVendor(vendor);
    setOpenLedgerDialog(true);
    await fetchVendorTransactions(vendor.id);
  };

  const handleCloseLedgerDialog = () => {
    setOpenLedgerDialog(false);
    setLedgerVendor(null);
    setTransactions([]);
  };

  const fetchVendorTransactions = async (vendorId: string) => {
    if (!currentUser) return;

    try {
      const allTransactions: Transaction[] = [];
      let runningBalance = 0;

      // Fetch purchases
      const allPurchases = await indexedDBService.getAllItems<Purchase>('purchases');
      const vendorPurchases = allPurchases.filter(p => p.vendorId === vendorId);

      // Convert purchases to transactions
      vendorPurchases.forEach(purchase => {
        runningBalance += purchase.totalAmount || 0;

        allTransactions.push({
          id: purchase.id,
          date: purchase.date,
          type: 'purchase',
          amount: purchase.totalAmount || 0,
          reference: purchase.invoiceNumber || purchase.id,
          status: purchase.status || 'unknown',
          balance: 0 // Will calculate later
        });
      });

      // Fetch vendor payments
      const allPayments = await indexedDBService.getAllItems<VendorPayment>('payments');
      // Assuming 'payments' store has vendorId set for vendor payments. 
      // If we are reusing 'payments' store, we must ensure it has vendorId field.
      // VendorPayment interface likely has vendorId? 
      // If not, we might be storing it blindly. 
      // The original code was using a subcollection, so vendorId was implicit in parent doc path.
      // Now we need explicit vendorId in the payment object. 
      const vendorPayments = allPayments.filter(p => p.vendorId === vendorId);

      // Convert payments to transactions
      vendorPayments.forEach(payment => {
        const amount = payment.amount || 0;

        allTransactions.push({
          id: payment.id,
          date: payment.date,
          type: 'payment',
          amount: -amount, // Negative amount for payments
          reference: payment.reference || payment.id,
          status: 'completed',
          balance: 0 // Will calculate later
        });
      });

      // Sort all transactions by date
      allTransactions.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

      // Recalculate running balance to account for merged sort
      runningBalance = 0;
      for (let i = 0; i < allTransactions.length; i++) {
        runningBalance += allTransactions[i].amount;
        allTransactions[i].balance = runningBalance;
      }

      setTransactions(allTransactions);
    } catch (error) {
      console.error('Error fetching vendor transactions:', error);
      setSnackbar({
        open: true,
        message: 'Error loading transaction ledger',
        severity: 'error'
      });
    } finally {
      setLoadingTransactions(false);
    }
  };

  const handleTransactionTypeFilterChange = (event: SelectChangeEvent<typeof transactionTypeFilter>) => {
    const value = event.target.value;
    setTransactionTypeFilter(typeof value === 'string' ? value.split(',') : value);
  };

  const handleTransactionStatusFilterChange = (event: SelectChangeEvent<typeof transactionStatusFilter>) => {
    const value = event.target.value;
    setTransactionStatusFilter(typeof value === 'string' ? value.split(',') : value);
  };

  const filteredTransactions = transactions.filter(transaction => {
    // Filter by date range
    if (startDate && new Date(transaction.date) < startDate) return false;
    if (endDate && new Date(transaction.date) > endDate) return false;

    // Filter by transaction type
    if (transactionTypeFilter.length > 0 && !transactionTypeFilter.includes(transaction.type)) return false;

    // Filter by status
    if (transactionStatusFilter.length > 0 && !transactionStatusFilter.includes(transaction.status)) return false;

    return true;
  });

  const exportLedgerToCSV = () => {
    if (!ledgerVendor || filteredTransactions.length === 0) return;

    const headers = ['Date', 'Type', 'Reference', 'Amount', 'Status', 'Running Balance'];

    const csvRows = [
      headers.join(','),
      ...filteredTransactions.map(transaction => {
        return [
          formatDate(transaction.date),
          transaction.type === 'purchase' ? 'Purchase' : 'Payment',
          transaction.reference,
          transaction.amount.toFixed(2),
          transaction.status,
          transaction.balance.toFixed(2)
        ].join(',');
      })
    ];

    const csvContent = "data:text/csv;charset=utf-8," + csvRows.join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `${ledgerVendor.name}_ledger_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Filter vendors based on search query
  const filteredVendors = vendors.filter(vendor => {
    if (!searchQuery) return true;

    const query = searchQuery.toLowerCase();
    return (
      vendor.name.toLowerCase().includes(query) ||
      vendor.contactPerson.toLowerCase().includes(query) ||
      vendor.email.toLowerCase().includes(query) ||
      vendor.phone.includes(query) ||
      vendor.gstin.toLowerCase().includes(query) ||
      vendor.vendorType.toLowerCase().includes(query)
    );
  });

  // Sort function
  const requestSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedVendors = () => {
    const sortableVendors = [...filteredVendors];
    if (sortConfig.key) {
      sortableVendors.sort((a, b) => {
        // Special case for pending amount
        if (sortConfig.key === 'pendingAmount') {
          const aAmount = vendorPayables[a.id] || 0;
          const bAmount = vendorPayables[b.id] || 0;
          return sortConfig.direction === 'asc' ? aAmount - bAmount : bAmount - aAmount;
        }

        // Handle undefined or null values
        const valueA = a[sortConfig.key as keyof Vendor];
        const valueB = b[sortConfig.key as keyof Vendor];

        if (valueA === undefined && valueB === undefined) return 0;
        if (valueA === undefined) return sortConfig.direction === 'asc' ? -1 : 1;
        if (valueB === undefined) return sortConfig.direction === 'asc' ? 1 : -1;

        if (valueA < valueB) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (valueA > valueB) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableVendors;
  };

  const toggleVendorExpansion = (vendorId: string) => {
    setExpandedVendor(expandedVendor === vendorId ? null : vendorId);
  };

  // Get formatted amount
  const formatAmount = (amount: number) => {
    return formatCurrency(amount);
  };

  // Add CSV export function
  const exportToCSV = () => {
    if (vendors.length === 0) return;

    const headers = ['Name', 'Type', 'GSTN', 'Email', 'Phone', 'Contact Person', 'City', 'State', 'Pending Amount'];

    const csvRows = [
      headers.join(','),
      ...getSortedVendors().map(vendor => {
        const pendingAmount = vendorPayables[vendor.id] || 0;
        return [
          vendor.name,
          vendor.vendorType,
          vendor.gstin ? formatGSTN(vendor.gstin) || 'N/A' : 'N/A',
          vendor.email || 'N/A',
          vendor.phone || 'N/A',
          vendor.contactPerson || 'N/A',
          vendor.city || 'N/A',
          vendor.state || 'N/A',
          pendingAmount.toFixed(2)
        ].join(',');
      })
    ];

    const csvContent = "data:text/csv;charset=utf-8," + csvRows.join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `vendors_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleOpenPaymentDialog = (vendor: Vendor) => {
    setSelectedVendorForPayment(vendor);
    setOpenPaymentDialog(true);
    setPaymentAmount(0);
    setPaymentMethod('Bank Transfer');
    setPaymentDate(new Date());
    setPaymentReference('');
    setPaymentNotes('');
    setPaymentAllocation('auto');
    setSelectedPurchases([]);

    // Fetch pending purchases for this vendor
    fetchPendingPurchases(vendor.id);
  };

  const handleClosePaymentDialog = () => {
    setOpenPaymentDialog(false);
    setSelectedVendorForPayment(null);
    setPendingPurchases([]);
    setSelectedPurchases([]);
  };

  const handleTogglePurchase = (purchaseId: string) => {
    const currentIndex = selectedPurchases.indexOf(purchaseId);
    const newSelectedPurchases = [...selectedPurchases];

    if (currentIndex === -1) {
      newSelectedPurchases.push(purchaseId);
    } else {
      newSelectedPurchases.splice(currentIndex, 1);
    }

    setSelectedPurchases(newSelectedPurchases);

    // Recalculate payment amount based on selected purchases if in auto mode
    if (paymentAllocation === 'auto') {
      const totalSelected = pendingPurchases
        .filter(p => newSelectedPurchases.includes(p.id))
        .reduce((sum, p) => sum + (p.totalAmount - (p.paidAmount || 0)), 0);
      setPaymentAmount(totalSelected);
    }
  };

  const handleSavePayment = async () => {
    if (!currentUser || !selectedVendorForPayment || paymentAmount <= 0 || selectedPurchases.length === 0) {
      setSnackbar({
        open: true,
        message: 'Please fill in all required fields',
        severity: 'error'
      });
      return;
    }

    try {
      // Create a new payment record
      // Ensure we add vendorId to the payment record for retrieval
      const paymentData: Partial<VendorPayment> & { vendorId: string } = {
        amount: paymentAmount,
        date: paymentDate instanceof Date ? paymentDate.toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        paymentMethod: paymentMethod,
        reference: paymentReference,
        notes: paymentNotes,
        purchaseIds: selectedPurchases,
        createdAt: new Date().toISOString(),
        vendorId: selectedVendorForPayment.id // Add vendorId explicit link
      };

      const paymentId = 'pay_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);

      // Add the payment to the payments store
      await indexedDBService.putItem('payments', {
        ...paymentData,
        id: paymentId
      });

      // Update the vendor's pending amount if we want to sync it, but it's calculated on fly usually.
      // But if we want to keep it in sync:
      const updatedVendor = {
        ...selectedVendorForPayment,
        pendingAmount: (selectedVendorForPayment.pendingAmount || 0) - paymentAmount
      };
      // We should probably rely on the purchase status instead of this field, but to be safe:
      await indexedDBService.putItem('vendors', updatedVendor);

      // Distribute the payment amount to selected purchases
      let remainingAmount = paymentAmount;

      // Sort purchases by due date (earliest first)
      const selectedPurchasesData = pendingPurchases
        .filter(p => selectedPurchases.includes(p.id))
        .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime());

      for (const purchase of selectedPurchasesData) {
        if (remainingAmount <= 0) break;

        const pendingForPurchase = purchase.totalAmount - (purchase.paidAmount || 0);

        let amountToApply = Math.min(pendingForPurchase, remainingAmount);
        remainingAmount -= amountToApply;

        const newPaidAmount = (purchase.paidAmount || 0) + amountToApply;
        const newStatus = newPaidAmount >= purchase.totalAmount ? 'paid' : 'pending';

        // Update the purchase record
        const updatedPurchase = {
          ...purchase,
          paidAmount: newPaidAmount,
          status: newStatus
        };
        await indexedDBService.putItem('purchases', updatedPurchase);
      }

      setSnackbar({
        open: true,
        message: 'Payment recorded successfully',
        severity: 'success'
      });

      handleClosePaymentDialog();
      fetchVendorPayables();
      fetchVendors();
    } catch (error) {
      console.error('Error recording payment:', error);
      setSnackbar({
        open: true,
        message: 'Error recording payment',
        severity: 'error'
      });
    }
  };

  const validateGSTN = (gstin: string) => {
    if (!gstin) return true; // GSTN is optional
    return isValidGSTN(gstin) ? '' : 'Invalid GSTN format';
  };

  return (
    <AnimatedPage>
      <Box sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1" sx={{
            background: 'linear-gradient(45deg, #009688 30%, #4DB6AC 90%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 'bold'
          }}>
            Vendor Management
          </Typography>

          <Box display="flex" alignItems="center">
            <TextField
              placeholder="Search vendors..."
              value={searchQuery}
              onChange={handleSearchChange}
              size="small"
              sx={{ mr: 2, width: 250 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchQuery ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchQuery('')}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{
                background: 'linear-gradient(45deg, #009688 30%, #4DB6AC 90%)',
                color: 'white',
                fontWeight: 'medium',
              }}
            >
              Add Vendor
            </Button>
          </Box>
        </Box>

        {/* Summary Cards */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} sm={6} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                height: '100%',
                borderRadius: 4,
                background: 'linear-gradient(135deg, rgba(0, 150, 136, 0.15) 0%, rgba(77, 182, 172, 0.05) 100%)',
                border: '1px solid rgba(0, 150, 136, 0.1)',
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                '&:hover': {
                  boxShadow: '0 8px 25px rgba(0, 150, 136, 0.15)',
                  transform: 'translateY(-5px)',
                }
              }}
            >
              <Box display="flex" alignItems="center" mb={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(0, 150, 136, 0.2)',
                    color: '#009688',
                    mr: 2
                  }}
                >
                  <StoreIcon fontSize="small" />
                </Box>
                <Typography variant="subtitle1" fontWeight="600" color="text.primary">
                  Total Vendors
                </Typography>
              </Box>

              <Typography variant="h5" fontWeight="bold" sx={{ mt: 2, color: '#009688' }}>
                {vendors.length}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                height: '100%',
                borderRadius: 4,
                background: 'linear-gradient(135deg, rgba(77, 182, 172, 0.15) 0%, rgba(0, 188, 212, 0.05) 100%)',
                border: '1px solid rgba(77, 182, 172, 0.1)',
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                '&:hover': {
                  boxShadow: '0 8px 25px rgba(77, 182, 172, 0.15)',
                  transform: 'translateY(-5px)',
                }
              }}
            >
              <Box display="flex" alignItems="center" mb={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(77, 182, 172, 0.2)',
                    color: '#4DB6AC',
                    mr: 2
                  }}
                >
                  <PaymentsIcon fontSize="small" />
                </Box>
                <Typography variant="subtitle1" fontWeight="600" color="text.primary">
                  Total Payables
                </Typography>
              </Box>

              <Typography variant="h5" fontWeight="bold" sx={{ mt: 2, color: '#4DB6AC' }}>
                {formatCurrency(totalPayables)}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                height: '100%',
                borderRadius: 4,
                background: 'linear-gradient(135deg, rgba(0, 188, 212, 0.15) 0%, rgba(3, 169, 244, 0.05) 100%)',
                border: '1px solid rgba(0, 188, 212, 0.1)',
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                '&:hover': {
                  boxShadow: '0 8px 25px rgba(0, 188, 212, 0.15)',
                  transform: 'translateY(-5px)',
                }
              }}
            >
              <Box display="flex" alignItems="center" mb={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(0, 188, 212, 0.2)',
                    color: '#00BCD4',
                    mr: 2
                  }}
                >
                  <PaymentIcon fontSize="small" />
                </Box>
                <Typography variant="subtitle1" fontWeight="600" color="text.primary">
                  Active Vendors
                </Typography>
              </Box>

              <Typography variant="h5" fontWeight="bold" sx={{ mt: 2, color: '#00BCD4' }}>
                {vendors.filter(vendor => vendor.isActive !== false).length}
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Actions Row */}
        <Box mb={3} display="flex" justifyContent="flex-end">
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={exportToCSV}
          >
            Export to CSV
          </Button>
        </Box>

        {/* Vendors Table */}
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{
            borderRadius: 2,
            boxShadow: '0 4px 20px 0 rgba(0,0,0,0.14)',
            overflowX: 'auto',
            width: '100%'
          }}>
            <Table>
              <TableHead>
                <TableRow sx={{ background: 'linear-gradient(45deg, #009688 30%, #4DB6AC 90%)' }}>
                  <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>
                    <TableSortLabel
                      active={sortConfig.key === 'name'}
                      direction={sortConfig.key === 'name' ? sortConfig.direction : 'asc'}
                      onClick={() => requestSort('name')}
                      sx={{ color: 'white', '&.MuiTableSortLabel-active': { color: 'white' } }}
                    >
                      Vendor Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>Contact Person</TableCell>
                  <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>Phone</TableCell>
                  <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>Email</TableCell>
                  <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>
                    <TableSortLabel
                      active={sortConfig.key === 'vendorType'}
                      direction={sortConfig.key === 'vendorType' ? sortConfig.direction : 'asc'}
                      onClick={() => requestSort('vendorType')}
                      sx={{ color: 'white', '&.MuiTableSortLabel-active': { color: 'white' } }}
                    >
                      Type
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>GSTN</TableCell>
                  <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>Outstanding</TableCell>
                  <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getSortedVendors().map((vendor) => (
                  <React.Fragment key={vendor.id}>
                    <TableRow hover>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Avatar
                            sx={{
                              bgcolor: 'primary.light',
                              width: 32,
                              height: 32,
                              mr: 1,
                              fontSize: '0.875rem'
                            }}
                          >
                            {vendor.name.charAt(0)}
                          </Avatar>
                          {vendor.name}
                        </Box>
                      </TableCell>
                      <TableCell>{vendor.contactPerson || '-'}</TableCell>
                      <TableCell>{vendor.phone || '-'}</TableCell>
                      <TableCell>{vendor.email || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={vendor.vendorType}
                          size="small"
                          sx={{
                            backgroundColor: 'rgba(0, 150, 136, 0.1)',
                            color: '#009688',
                            fontWeight: 'medium'
                          }}
                        />
                      </TableCell>
                      <TableCell>{vendor.gstin ? formatGSTN(vendor.gstin) : '-'}</TableCell>
                      <TableCell>
                        {vendorPayables[vendor.id] ? (
                          <Typography fontWeight="bold" color="error.main">
                            {formatCurrency(vendorPayables[vendor.id])}
                          </Typography>
                        ) : (
                          <Typography color="success.main">
                            No outstanding
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={1}>
                          <Tooltip title="View Transaction History">
                            <IconButton size="small" onClick={() => handleOpenLedgerDialog(vendor)}>
                              <HistoryIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {vendorPayables[vendor.id] > 0 && (
                            <Tooltip title="Make Payment">
                              <IconButton size="small" onClick={() => handleOpenPaymentDialog(vendor)}>
                                <PaymentIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Edit Vendor">
                            <IconButton size="small" onClick={() => handleOpenDialog(vendor)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Vendor">
                            <IconButton size="small" onClick={() => handleDeleteVendor(vendor.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
                {getSortedVendors().length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                      <Typography variant="subtitle1" color="text.secondary">
                        No vendors found matching your criteria
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Add/Edit Vendor Dialog */}
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>
            {selectedVendor ? 'Edit Vendor' : 'Add New Vendor'}
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  name="name"
                  label="Vendor Name"
                  value={newVendor.name}
                  onChange={handleInputChange}
                  fullWidth
                  margin="normal"
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Vendor Type</InputLabel>
                  <Select
                    name="vendorType"
                    value={newVendor.vendorType}
                    label="Vendor Type"
                    onChange={handleSelectChange}
                  >
                    {VENDOR_TYPES.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  name="contactPerson"
                  label="Contact Person"
                  value={newVendor.contactPerson}
                  onChange={handleInputChange}
                  fullWidth
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  name="gstin"
                  label="GSTN"
                  value={newVendor.gstin}
                  onChange={(e) => setNewVendor({ ...newVendor, gstin: e.target.value })}
                  error={Boolean(newVendor.gstin && validateGSTN(newVendor.gstin))}
                  helperText={newVendor.gstin ? validateGSTN(newVendor.gstin) : ''}
                  placeholder="22AAAAA0000A1Z5"
                  fullWidth
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  name="email"
                  label="Email"
                  type="email"
                  value={newVendor.email}
                  onChange={handleInputChange}
                  fullWidth
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  name="phone"
                  label="Phone"
                  value={newVendor.phone}
                  onChange={handleInputChange}
                  fullWidth
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="address"
                  label="Address"
                  value={newVendor.address}
                  onChange={handleInputChange}
                  fullWidth
                  multiline
                  rows={2}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  name="city"
                  label="City"
                  value={newVendor.city}
                  onChange={handleInputChange}
                  fullWidth
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  name="state"
                  label="State"
                  value={newVendor.state}
                  onChange={handleInputChange}
                  fullWidth
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  name="pincode"
                  label="Pincode"
                  value={newVendor.pincode}
                  onChange={handleInputChange}
                  fullWidth
                  margin="normal"
                />
              </Grid>

              <Grid item xs={12}>
                <Divider textAlign="left" sx={{ mt: 2, mb: 1 }}>
                  <Typography variant="subtitle2">Payment Details</Typography>
                </Divider>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Payment Terms</InputLabel>
                  <Select
                    name="paymentTerms"
                    value={newVendor.paymentTerms}
                    label="Payment Terms"
                    onChange={handleSelectChange}
                  >
                    {PAYMENT_TERMS.map((term) => (
                      <MenuItem key={term} value={term}>
                        {term}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  name="accountNumber"
                  label="Account Number"
                  value={newVendor.accountNumber}
                  onChange={handleInputChange}
                  fullWidth
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  name="bankName"
                  label="Bank Name"
                  value={newVendor.bankName}
                  onChange={handleInputChange}
                  fullWidth
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  name="ifscCode"
                  label="IFSC Code"
                  value={newVendor.ifscCode}
                  onChange={handleInputChange}
                  fullWidth
                  margin="normal"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSaveVendor}
              sx={{
                backgroundImage: 'var(--primary-gradient)',
              }}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Payment Dialog */}
        <Dialog open={openPaymentDialog} onClose={handleClosePaymentDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            Record Payment for {selectedVendorForPayment?.name}
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Payment Amount"
                  type="number"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(Number(e.target.value))}
                  fullWidth
                  margin="normal"
                  required
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Payment Date"
                  value={paymentDate}
                  onChange={(newDate) => setPaymentDate(newDate)}
                  slotProps={{ textField: { fullWidth: true, margin: "normal" } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal" required>
                  <InputLabel>Payment Method</InputLabel>
                  <Select
                    value={paymentMethod}
                    label="Payment Method"
                    onChange={(e) => setPaymentMethod(e.target.value)}
                  >
                    {PAYMENT_METHODS.map((method) => (
                      <MenuItem key={method} value={method}>
                        {method}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Reference Number"
                  value={paymentReference}
                  onChange={(e) => setPaymentReference(e.target.value)}
                  fullWidth
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Notes"
                  value={paymentNotes}
                  onChange={(e) => setPaymentNotes(e.target.value)}
                  fullWidth
                  margin="normal"
                  multiline
                  rows={2}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                  Payment Allocation
                </Typography>

                <RadioGroup
                  row
                  value={paymentAllocation}
                  onChange={(e) => setPaymentAllocation(e.target.value as 'auto' | 'manual')}
                >
                  <FormControlLabel
                    value="auto"
                    control={<Radio />}
                    label="Apply to selected invoices (automatic)"
                  />
                  <FormControlLabel
                    value="manual"
                    control={<Radio />}
                    label="Enter custom amount (manual)"
                  />
                </RadioGroup>

                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                  Select the invoices you want to apply this payment to:
                </Typography>

                <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
                  {pendingPurchases.length === 0 ? (
                    <Typography sx={{ p: 2 }} color="text.secondary">
                      No pending invoices found for this vendor.
                    </Typography>
                  ) : (
                    pendingPurchases.map((purchase) => {
                      const pendingAmount = purchase.totalAmount - (purchase.paidAmount || 0);
                      return (
                        <ListItem key={purchase.id} dense>
                          <Checkbox
                            edge="start"
                            checked={selectedPurchases.indexOf(purchase.id) !== -1}
                            onChange={() => handleTogglePurchase(purchase.id)}
                            disabled={paymentAmount <= 0}
                          />
                          <ListItemText
                            primary={`Invoice #${purchase.invoiceNumber}`}
                            secondary={
                              <Typography variant="body2" color="text.secondary">
                                Due: {formatDate(purchase.dueDate)} | Amount: {formatAmount(purchase.totalAmount)} | Pending: {formatAmount(pendingAmount)}
                              </Typography>
                            }
                          />
                        </ListItem>
                      );
                    })
                  )}
                </List>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClosePaymentDialog}>Cancel</Button>
            <Button
              onClick={handleSavePayment}
              variant="contained"
              disabled={paymentAmount <= 0 || selectedPurchases.length === 0}
              sx={{
                backgroundImage: 'var(--primary-gradient)',
              }}
            >
              Record Payment
            </Button>
          </DialogActions>
        </Dialog>

        {/* Transaction Ledger Dialog */}
        <Dialog open={openLedgerDialog} onClose={handleCloseLedgerDialog} maxWidth="lg" fullWidth>
          <DialogTitle>
            Transaction Ledger: {ledgerVendor?.name}
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Filter Transactions
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <DatePicker
                    label="Start Date"
                    value={startDate}
                    onChange={(date) => setStartDate(date)}
                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <DatePicker
                    label="End Date"
                    value={endDate}
                    onChange={(date) => setEndDate(date)}
                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Transaction Type</InputLabel>
                    <Select
                      multiple
                      value={transactionTypeFilter}
                      onChange={handleTransactionTypeFilterChange}
                      label="Transaction Type"
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip
                              key={value}
                              label={value === 'purchase' ? 'Purchase' : 'Payment'}
                              size="small"
                            />
                          ))}
                        </Box>
                      )}
                    >
                      <MenuItem value="purchase">Purchase</MenuItem>
                      <MenuItem value="payment">Payment</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      multiple
                      value={transactionStatusFilter}
                      onChange={handleTransactionStatusFilterChange}
                      label="Status"
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip key={value} label={value} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="paid">Paid</MenuItem>
                      <MenuItem value="draft">Draft</MenuItem>
                      <MenuItem value="completed">Completed</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Transactions History
              </Typography>
              <Button
                startIcon={<FileDownloadIcon />}
                onClick={exportLedgerToCSV}
                variant="outlined"
                disabled={filteredTransactions.length === 0}
              >
                Export Ledger
              </Button>
            </Box>

            {loadingTransactions ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : filteredTransactions.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <ReceiptIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No transactions found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  There are no transactions matching your filters or this vendor has no transaction history.
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Reference</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Running Balance</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredTransactions.map((transaction) => (
                      <TableRow key={transaction.id}>
                        <TableCell>{formatDate(transaction.date)}</TableCell>
                        <TableCell>
                          <Chip
                            label={transaction.type === 'purchase' ? 'Purchase' : 'Payment'}
                            color={transaction.type === 'purchase' ? 'info' : 'success'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{transaction.reference}</TableCell>
                        <TableCell align="right" sx={{
                          color: transaction.amount < 0 ? 'success.main' : 'error.main',
                          fontWeight: 'medium'
                        }}>
                          {formatAmount(transaction.amount)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={transaction.status}
                            color={
                              transaction.status === 'paid' || transaction.status === 'completed' ? 'success' :
                                transaction.status === 'pending' ? 'warning' :
                                  transaction.status === 'draft' ? 'default' : 'default'
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          {formatAmount(transaction.balance)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseLedgerDialog}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </AnimatedPage>
  );
};

export default Vendors; 
